/**
 * PATH: packages/email-hub/src/feature/components/rich-text/resizableImage.tsx
 * FILE_ID: EMAILHUB-RICHTEXT-RESIZEIMG-001
 * PURPOSE: Image node with click-to-select + corner drag-resize + align,
 *          preserving the legacy editor's inline-image UX on TipTap. Width
 *          is stored as an inline style so it round-trips and survives the
 *          email-safe serialiser.
 */

import { useRef, useState } from 'react'
import { Image } from '@tiptap/extension-image'
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react'
import type { NodeViewProps } from '@tiptap/react'
import { TextSelection } from '@tiptap/pm/state'
import { AlignCenter, AlignLeft, AlignRight } from 'lucide-react'

function ImageView({ node, updateAttributes, selected, editor }: NodeViewProps) {
  const { src, alt, width, align } = node.attrs as {
    src: string; alt?: string; width?: string | null; align?: string | null
  }
  const imgRef = useRef<HTMLImageElement | null>(null)
  const [dragging, setDragging] = useState(false)

  const startResize = (e: React.PointerEvent, corner: 'nw' | 'ne' | 'sw' | 'se') => {
    e.preventDefault(); e.stopPropagation()
    const img = imgRef.current
    if (!img) return
    setDragging(true)
    const startX = e.clientX
    const startW = img.getBoundingClientRect().width
    const grows = corner === 'ne' || corner === 'se'
    const max = img.parentElement?.parentElement?.clientWidth || 800
    const onMove = (ev: PointerEvent) => {
      const dx = ev.clientX - startX
      const next = Math.max(40, Math.min(Math.round(startW + (grows ? dx : -dx)), max))
      updateAttributes({ width: `${next}px` })
    }
    const onUp = () => {
      setDragging(false)
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  // Align via the BLOCK wrapper's `text-align`: the image sits in an
  // `inline-block` span, and `text-align` reliably positions an inline-block
  // child for left/center/right alike. (The previous `margin:auto` approach
  // silently failed — auto margins don't horizontally align an inline-block,
  // so only the wrapper's hard-coded `center` case ever worked. Operator
  // 2026-06-29: "alignment doesn't work for images".)
  const wrapAlign = align === 'center' || align === 'right' || align === 'left'
    ? (align as 'left' | 'center' | 'right')
    : undefined

  return (
    <NodeViewWrapper className="qte-img-wrap" style={{ display: 'block', position: 'relative', textAlign: wrapAlign }}>
      <span style={{ position: 'relative', display: 'inline-block' }}>
        <img
          ref={imgRef}
          src={src}
          alt={alt || ''}
          className="qte-img"
          style={{ width: width || undefined, maxWidth: '100%', height: 'auto', display: 'block', outline: selected ? '2px solid #6366f1' : undefined, borderRadius: 4 }}
          draggable={false}
        />
        {selected && editor?.isEditable && (
          <>
            {(['nw', 'ne', 'sw', 'se'] as const).map((c) => (
              <span key={c} onPointerDown={(e) => startResize(e, c)}
                style={{
                  position: 'absolute', height: 12, width: 12, margin: -6, borderRadius: 3,
                  border: '1px solid #fff', background: '#6366f1', boxShadow: '0 1px 3px rgba(0,0,0,.3)',
                  top: c[0] === 'n' ? 0 : undefined, bottom: c[0] === 's' ? 0 : undefined,
                  left: c[1] === 'w' ? 0 : undefined, right: c[1] === 'e' ? 0 : undefined,
                  cursor: c === 'nw' || c === 'se' ? 'nwse-resize' : 'nesw-resize', zIndex: 20,
                }} />
            ))}
            {!dragging && (
              <span style={{ position: 'absolute', top: -34, left: 0, display: 'flex', gap: 2, padding: 2, borderRadius: 6, border: '1px solid var(--border, #e5e7eb)', background: 'var(--popover, #fff)', boxShadow: '0 2px 6px rgba(0,0,0,.15)' }}>
                <AlignBtn icon={AlignLeft} onClick={() => updateAttributes({ align: 'left' })} />
                <AlignBtn icon={AlignCenter} onClick={() => updateAttributes({ align: 'center' })} />
                <AlignBtn icon={AlignRight} onClick={() => updateAttributes({ align: 'right' })} />
              </span>
            )}
          </>
        )}
      </span>
    </NodeViewWrapper>
  )
}

function AlignBtn({ icon: Icon, onClick }: { icon: React.ComponentType<{ className?: string }>; onClick: () => void }) {
  return (
    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick}
      className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground">
      <Icon className="h-3.5 w-3.5" />
    </button>
  )
}

export const ResizableImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (el) => (el as HTMLElement).style.width || el.getAttribute('width') || null,
        renderHTML: (attrs) => (attrs.width ? { style: `width:${attrs.width as string};max-width:100%;height:auto;` } : {}),
      },
      align: {
        default: null,
        parseHTML: (el) => (el as HTMLElement).getAttribute('data-align'),
        renderHTML: (attrs) => (attrs.align ? { 'data-align': attrs.align as string } : {}),
      },
    }
  },
  addNodeView() {
    return ReactNodeViewRenderer(ImageView)
  },
  // Backspace/Delete with the caret sitting next to an image (NOT click-
  // selected) used to require two presses — ProseMirror's default first
  // turns the block image into a NodeSelection, then a second press deletes
  // it — or appeared to do nothing, so operators reported "delete doesn't
  // remove the image". Delete the adjacent image in a single press when the
  // caret is at the matching edge of its textblock. Returns false in every
  // other case so normal character deletion and click-to-select+delete are
  // completely unchanged.
  addKeyboardShortcuts() {
    const ext = this
    const removeAdjacentImage = (dir: 'before' | 'after') => (): boolean => {
      const { state, view } = ext.editor
      const { selection } = state
      // Only act on a collapsed text caret. A click-selected image is a
      // NodeSelection (already deleted by the default keymap) and a range
      // selection should delete its own contents.
      if (!(selection instanceof TextSelection) || !selection.empty) return false
      const $cursor = selection.$from
      // The caret must be at the very edge of its textblock in the press
      // direction; otherwise a real character is there to delete first.
      if (dir === 'before' && $cursor.parentOffset !== 0) return false
      if (dir === 'after' && $cursor.parentOffset !== $cursor.parent.content.size) return false
      const edge = dir === 'before' ? $cursor.before() : $cursor.after()
      const $edge = state.doc.resolve(edge)
      const neighbour = dir === 'before' ? $edge.nodeBefore : $edge.nodeAfter
      if (!neighbour || neighbour.type.name !== ext.name) return false
      const from = dir === 'before' ? edge - neighbour.nodeSize : edge
      view.dispatch(state.tr.delete(from, from + neighbour.nodeSize).scrollIntoView())
      return true
    }
    return {
      Backspace: removeAdjacentImage('before'),
      Delete: removeAdjacentImage('after'),
    }
  },
}).configure({ inline: false, allowBase64: true, HTMLAttributes: { class: 'qte-img' } })
